import React, {useContext, useState} from 'react';
import {View, Pressable, Dimensions} from 'react-native';
import {BasePage} from './helpers/BasePage';
import {FindifySimpleHeader} from './components/FindifySimpleHeader';
import {FindifyTopBarOverlay} from './components/FindifyTopBarOverlay';
import {PlaceColors} from './helpers/PlaceColors';
import {FindifyDataContext} from '@/contexts/FindifyDataContext';
import {DefaultShadow} from './helpers/DefaultShadows';
import {PlaceText} from './components/PlaceText';
import Icon from 'react-native-vector-icons/Ionicons';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useTranslation} from 'react-i18next';

export const TicTacToePage = ({navigation}: any) => {
  const findifyDataContext = useContext(FindifyDataContext);
  const insets = useSafeAreaInsets();
  const {t} = useTranslation();

  const [board, setBoard] = useState(Array(9).fill(null));
  const [isXNext, setIsXNext] = useState(true);

  const calculateWinner = (squares: any[]) => {
    const lines = [
      [0, 1, 2],
      [3, 4, 5],
      [6, 7, 8], // rows
      [0, 3, 6],
      [1, 4, 7],
      [2, 5, 8], // cols
      [0, 4, 8],
      [2, 4, 6], // diagonals
    ];
    for (let i = 0; i < lines.length; i++) {
      const [a, b, c] = lines[i];
      if (
        squares[a] &&
        squares[a] === squares[b] &&
        squares[a] === squares[c]
      ) {
        return squares[a];
      }
    }
    return null;
  };

  const winner = calculateWinner(board);
  const isDraw = !winner && board.every(square => square !== null);

  const handlePress = (index: number) => {
    if (board[index] || winner) {
      return;
    }

    const newBoard = [...board];
    newBoard[index] = isXNext ? 'X' : 'O';
    setBoard(newBoard);
    setIsXNext(!isXNext);
  };

  const resetGame = () => {
    setBoard(Array(9).fill(null));
    setIsXNext(true);
  };

  const screenWidth = Dimensions.get('window').width;
  const boardSize = screenWidth * 0.85;
  const tileSize = (boardSize - 40) / 3;

  return (
    <BasePage navigation={navigation}>
      <FindifyTopBarOverlay />
      <FindifySimpleHeader
        navigation={navigation}
        title={t('games.tictactoe.title', {defaultValue: 'Tic Tac Toe'})}
      />

      <View
        style={{
          flex: 1,
          backgroundColor: PlaceColors(findifyDataContext).background.get(),
          alignItems: 'center',
          justifyContent: 'center',
          paddingBottom: insets.bottom + 60,
        }}>
        <View style={{marginBottom: 40, alignItems: 'center'}}>
          <PlaceText
            style={{
              fontSize: 28,
              fontWeight: 'bold',
              color: PlaceColors(findifyDataContext).text.get(),
            }}>
            {winner
              ? t('games.tictactoe.winner', {
                  defaultValue: 'Winner: {{player}}',
                  player: winner,
                })
              : isDraw
              ? t('games.tictactoe.draw', {defaultValue: "It's a Draw!"})
              : t('games.tictactoe.turn', {
                  defaultValue: "Player's Turn: {{player}}",
                  player: isXNext ? 'X' : 'O',
                })}
          </PlaceText>
        </View>

        <View
          style={{
            width: boardSize,
            height: boardSize,
            flexDirection: 'row',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignContent: 'space-between',
          }}>
          {board.map((cell, index) => (
            <Pressable
              key={index}
              style={[
                {
                  width: tileSize,
                  height: tileSize,
                  backgroundColor:
                    PlaceColors(findifyDataContext).primary.withAlpha(200),
                  borderRadius: 16,
                  alignItems: 'center',
                  justifyContent: 'center',
                },
                DefaultShadow,
              ]}
              onPress={() => handlePress(index)}>
              {!!cell && (
                <PlaceText
                  style={{
                    fontSize: 48,
                    fontWeight: 'bold',
                    color:
                      cell === 'X'
                        ? PlaceColors(findifyDataContext).findifyPrimary.get()
                        : PlaceColors(findifyDataContext).background.get(),
                  }}>
                  {cell}
                </PlaceText>
              )}
            </Pressable>
          ))}
        </View>

        <Pressable
          style={[
            {
              marginTop: 40,
              backgroundColor:
                PlaceColors(findifyDataContext).findifyPrimary.get(),
              paddingHorizontal: 40,
              paddingVertical: 16,
              borderRadius: 24,
              flexDirection: 'row',
              alignItems: 'center',
              gap: 10,
            },
            DefaultShadow,
          ]}
          onPress={resetGame}
          hitSlop={{top: 10, bottom: 10, left: 10, right: 10}}>
          <Icon name="refresh" size={24} color="#FFF" />
          <PlaceText style={{color: '#FFF', fontSize: 20, fontWeight: 'bold'}}>
            {t('games.tictactoe.play_again', {defaultValue: 'Play Again'})}
          </PlaceText>
        </Pressable>
      </View>
    </BasePage>
  );
};
